// Hot cues and loops (P3.10) — FR-03, FR-04.
//
// **The arithmetic, pure and shared.** The loop machine itself belongs to the audio thread — FR-04's
// boundaries are transport state and "a loop that wraps on the main thread's schedule clicks" — and
// nothing there can be tested. So the audio thread gets the wrapping and this gets the decisions:
// where a loop starts, whether it snaps to the grid, and what a cue slot is allowed to hold.
//
// Here rather than in /audio/dsp for the reason P3.9's `sync.ts` was moved: the UI needs half of it
// (which loop lengths exist, what a slot holds) and `ui-no-engine-internals` exists so DSP code is
// not dragged into the app bundle. None of this is signal processing.

import type { SyncGrid } from './sync';
import { isSyncableGrid } from './sync';

/**
 * FR-03's number: **eight**, and the prompt is explicit that it is "not seven and not 'expandable
 * later'".
 *
 * Eight is what a hardware controller has a row of, and the number a hand learns. A cue store that
 * grew would be a UI that changes shape between tracks.
 */
export const HOT_CUE_COUNT = 8;

/** FR-04's auto-loop lengths, in beats. */
export const AUTO_LOOP_BEATS = [1, 2, 4, 8, 16] as const;
export type AutoLoopBeats = (typeof AUTO_LOOP_BEATS)[number];

/**
 * An empty cue slot.
 *
 * `NaN` rather than `null` or `-1`, because the audio thread stores these in a `Float64Array` that
 * is allocated once and never replaced — FR-04's "the audio thread allocates nothing" applies to
 * cues too. `NaN` is the only value in a float array that cannot be mistaken for a position, and
 * every comparison against it is false, which is the behaviour a jump to an empty slot wants.
 */
export const EMPTY_CUE = Number.NaN;

export function isSetCue(frames: number): boolean {
  return Number.isFinite(frames) && frames >= 0;
}

/** A loop's boundaries, in frames. */
export interface LoopRegion {
  readonly startFrames: number;
  readonly endFrames: number;
}

/**
 * How a loop's boundaries were decided (P3.10) — FR-04's conditional quantization, made visible.
 *
 * P3.10's rule: "a track with no grid, or a low-confidence one, must still loop — unquantized, and
 * **honestly so**." Honestly means the user can tell, so the reason travels with the loop rather
 * than being inferred from whether the numbers look round.
 */
export type LoopQuantization =
  'beat' | 'unquantized-no-grid' | 'unquantized-low-confidence';

/**
 * How sure the tempo must be before a loop is allowed to snap to its grid.
 *
 * **The same threshold the BPM readout uses to stop hedging** (`TEMPO_SURE_ENOUGH` in
 * `TrackLoader.tsx`), and deliberately so: a grid the UI is showing with a question mark is a grid
 * a loop must not silently trust. Snapping to a wrong grid moves a loop the user placed by ear,
 * which is worse than not snapping at all — FR-13's manual tools exist for exactly the tracks this
 * gets wrong.
 */
export const LOOP_QUANTIZE_CONFIDENCE = 0.6;

/**
 * Whether this track's grid may be used to quantize, and why not when it may not.
 *
 * Both refusals are real: `no-grid` is a track with no detected tempo at all, and `low-confidence`
 * is a tempo the detector reported while saying it was guessing. P3.3 measured roughly one track in
 * twenty coming out wrong, and P3.4 found the confidence does not predict correctness as well as it
 * should — so this threshold is a floor on trust, not a guarantee.
 */
export function loopQuantization(
  grid: SyncGrid | null,
  confidence: number,
): LoopQuantization {
  if (!isSyncableGrid(grid)) {
    return 'unquantized-no-grid';
  }
  return confidence >= LOOP_QUANTIZE_CONFIDENCE ? 'beat' : 'unquantized-low-confidence';
}

/**
 * The nearest beat to a position, in frames.
 *
 * **Nearest, not previous.** A DJ pressing a 4-beat loop 10 ms after the beat means *this* beat, and
 * snapping backwards would jump the loop almost a whole beat earlier than the sound they reacted to.
 * Rounding is what "on the grid" means to a hand.
 */
export function quantizeToBeat(
  positionFrames: number,
  grid: SyncGrid,
  sampleRateHz: number,
): number {
  const seconds = positionFrames / sampleRateHz;
  const beats = (seconds - grid.anchorSeconds) / grid.beatSeconds;
  const snapped = grid.anchorSeconds + Math.round(beats) * grid.beatSeconds;
  return snapped * sampleRateHz;
}

/**
 * The loop an auto-length button makes from the current position.
 *
 * Quantized when the grid is trustworthy, and **exactly where it was set** when it is not — which is
 * FR-04's conditional read literally. An unquantized auto-loop still has the right *length* in
 * seconds; only its start is unsnapped, because the length comes from the tempo either way and a
 * track with no tempo has no auto-loop at all.
 *
 * Returns null when the loop cannot be made: no grid means no beat length to make it from, and a
 * loop longer than the track is not a loop.
 */
export function autoLoop(
  positionFrames: number,
  beats: number,
  grid: SyncGrid | null,
  quantization: LoopQuantization,
  sampleRateHz: number,
  frameCount: number,
): LoopRegion | null {
  if (!isSyncableGrid(grid) || beats <= 0 || sampleRateHz <= 0) {
    return null;
  }

  const lengthFrames = beats * grid.beatSeconds * sampleRateHz;
  if (lengthFrames < 1 || lengthFrames > frameCount) {
    return null;
  }

  const rawStart =
    quantization === 'beat'
      ? quantizeToBeat(positionFrames, grid, sampleRateHz)
      : positionFrames;

  // Clamped so the whole loop is inside the track. Snapping can push the start forward past the end
  // of a track being looped near its final beat, and a loop that runs off the end would freeze at
  // the last frame rather than repeat.
  const start = Math.min(Math.max(0, rawStart), Math.max(0, frameCount - lengthFrames));
  return { startFrames: start, endFrames: start + lengthFrames };
}

/**
 * A manually set loop, from an in point and an out point.
 *
 * Ordered rather than trusted: a DJ who sets out before in means the region between them, and
 * refusing would be pedantry about which button came first. A zero-length region is rejected, since
 * a loop with no content is silence that never advances.
 */
export function manualLoop(
  inFrames: number,
  outFrames: number,
  minimumFrames: number,
): LoopRegion | null {
  const start = Math.min(inFrames, outFrames);
  const end = Math.max(inFrames, outFrames);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end - start < minimumFrames) {
    return null;
  }
  return { startFrames: start, endFrames: end };
}

/**
 * Where the playhead lands after a step that may have crossed a loop boundary.
 *
 * **All numbers, no object**, and called once per sample while a loop is active — so the shape
 * matters as much as the arithmetic. A monomorphic function over four doubles is something V8 will
 * inline; anything carrying a `LoopRegion` would allocate or at best chase a pointer 48,000 times a
 * second on the thread that must do neither.
 *
 * Handles reverse (`rate < 0`) because the deck does: P1.1's resampler runs backwards, so a looped
 * deck scrubbed in reverse must wrap at the *start* and come back round to the end.
 *
 * A `while` rather than an `if`: at very high rates a single step can cross a short loop more than
 * once, and one subtraction would leave the playhead outside the loop it is supposed to be inside.
 */
export function wrapIntoLoop(
  position: number,
  startFrames: number,
  endFrames: number,
): number {
  const length = endFrames - startFrames;
  if (length <= 0) {
    return position;
  }
  let wrapped = position;
  while (wrapped >= endFrames) {
    wrapped -= length;
  }
  while (wrapped < startFrames) {
    wrapped += length;
  }
  return wrapped;
}

/**
 * How long the crossfade at a loop wrap should be, in frames.
 *
 * **A loop wrap is a waveform discontinuity, and a discontinuity is a click** — FR-04's verify names
 * the worst case, "1 beat at high tempo", where the wrap comes round three times a second. Beat
 * alignment does not save it: the sample either side of the seam is whatever the music was doing,
 * and the jump between them is a step.
 *
 * So the last few milliseconds before the end are blended with the same few milliseconds before the
 * start, which is the standard fix and costs one extra read and a lerp per sample inside the window.
 *
 * Five milliseconds, capped at a quarter of the loop: long enough to turn a step into a slope below
 * anything audible as a click, short enough that a 1-beat loop at 174 BPM (345 ms) spends 1.4% of
 * itself crossfading. The cap is what keeps that ratio honest for loops shorter than any beat.
 */
export const LOOP_CROSSFADE_SECONDS = 0.005;

export function loopCrossfadeFrames(
  startFrames: number,
  endFrames: number,
  sampleRateHz: number,
): number {
  const length = endFrames - startFrames;
  if (length <= 0) {
    return 0;
  }
  return Math.min(LOOP_CROSSFADE_SECONDS * sampleRateHz, length / 4);
}
